import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id_pendaftaran: string;
  nama_pendaftar: string;
  no_peserta: string;
  hp: string;
  event: string;
  nama_event: string;
  paket: string;
  instansi: string;
}

interface Panitia {
  id_panitia: number;
  username: string;
  nama_panitia: string;
  email: string;
  no_hp: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  panitia: Panitia | null;
  login: (userData: User) => Promise<boolean>;
  loginPanitia: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  logoutPanitia: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [panitia, setPanitia] = useState<Panitia | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    try {
      const savedUser = localStorage.getItem('user');
      const savedPanitia = localStorage.getItem('panitia');
      
      if (savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
        setUser(JSON.parse(savedUser));
      }
      if (savedPanitia && savedPanitia !== 'null' && savedPanitia !== 'undefined') {
        setPanitia(JSON.parse(savedPanitia));
      }
    } catch (error) {
      console.error('Error parsing localStorage data:', error);
      // Clear invalid data
      localStorage.removeItem('user');
      localStorage.removeItem('panitia');
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (userData: User): Promise<boolean> => {
    try {
      setLoading(true);
      console.log('Setting user data:', userData);

        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return true;
    } catch (error: any) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loginPanitia = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      console.log('Sending panitia login request...');
      
      const response = await axios.post('https://event.petirs.id/backend/api/panitia-login.php', {
        username,
        password
      });

      console.log('Panitia login response:', response.data);

      if (response.data.success) {
        const panitiaData = response.data.data;
        console.log('Setting panitia data:', panitiaData);
        setPanitia(panitiaData);
        localStorage.setItem('panitia', JSON.stringify(panitiaData));
        return true;
      } else {
        console.log('Panitia login failed:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Panitia login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const logoutPanitia = () => {
    setPanitia(null);
    localStorage.removeItem('panitia');
  };

  const value = {
    user,
    panitia,
    login,
    loginPanitia,
    logout,
    logoutPanitia,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 